import React, {FunctionComponent, useEffect} from "react";
import {makeStyles} from "tss-react/mui";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import moment from "moment";
import BookingPopover from "@/Domains/DispositionManagement/Components/Popovers/BookingPopover";
import {MOVE_BOOKING, OPEN_BOOKING_INFO,} from "@/Redux/Disposition/Constants";
import {Theme} from "@mui/material";
import {useAppDispatch, useAppSelector} from "@/Redux/Redux";
import {useDisplayMessage} from "@/Utils/CustomHooks/useDisplayMessage";
import {useDrag} from "react-dnd";
import {getEmptyImage} from "react-dnd-html5-backend";
import {STATIC_COL_WIDTH} from "@/Domains/DispositionManagement/Constants";
import {RootStateOrAny, useSelector} from "react-redux";
import {selectHasMinimalRights, ViewerState,} from "@/Redux/Auth/AuthReducer";
import MoveBookingMutation from "@/Domains/DispositionManagement/Mutations/MoveBookingMutation";
import {useConfirm} from "material-ui-confirm";
import InformationConfirmDialog from "@/Components/ConfirmationModal/InformationConfirmDialog";
import {logger} from "@/Logger";
import LockIcon from "@mui/icons-material/Lock";
import {useTranslation} from "@/Utils/CustomHooks/useTranslation";
import {selectColors} from "@/Redux/App/AppReducer";

interface BookingLineProps {
    lineItem: any;
    setDragDropClass: boolean | null;
    contentWidth: number | undefined;
    changeItem: any;
}

const useStyles = makeStyles()((theme: Theme) => ({
    draggable: {
        height: 52,
        touchAction: "none",
        position: "absolute",
        overflow: "hidden",
        zIndex: 50,
        userSelect: "none",
        cursor: "pointer",
        marginLeft: 306,
    },

    booking: {
        fontSize: 12,
        padding: "6px 8px",
        margin: "0",
        border: "3px solid transparent",
        height: "52px",
        borderRadius: 2,
        pointerEvents: "none",
        userSelect: "none",
        whiteSpace: "nowrap",
        overflow: "hidden",
        color: "#000",
    },
}));

const BookingLine: FunctionComponent<BookingLineProps> = ({
                                                              lineItem,
                                                              setDragDropClass,
                                                              contentWidth,
                                                              changeItem
                                                          }): JSX.Element => {
    const {classes} = useStyles();
    //const dispositionData = useAppSelector(state => state.disposition);
    const {
        dispositionStartdate,
        dispositionEnddate,
        dayWidth,
        dispoWidth,
        singleDay,
        selectedStation
    } = useAppSelector((state) => state.disposition);

    const colors = useSelector((state) =>
        selectColors(state as RootStateOrAny)
    );

    const deliveryAndPickupPrivateColor = colors.data.filter(
        (item: any) => item.code === "deliveryColPriv"
    )[0]?.color;

    const partialInvoiceColor = colors.data.filter(
        (item: any) => item.code === "COL_ABSCHLAG"
    )[0]?.color;

    const hasMinimalRights = useSelector((state) =>
        selectHasMinimalRights(state as ViewerState),
    );

    if (contentWidth === undefined) {
        contentWidth = 0;
    } else {
        contentWidth -= STATIC_COL_WIDTH;
    }
    const {displayMessage} = useDisplayMessage();
    const {t} = useTranslation();

    const [popoverPositionX, setPopoverPositionX] = React.useState<number>(0);
    const [popoverPositionY, setPopoverPositionY] = React.useState<number>(0);
    const [popoverIsOpen, setPopoverIsOpen] = React.useState<boolean>(false);
    const confirm = useConfirm();

    const dispatch = useAppDispatch();

    const handlePopoverOpen = (
        event: React.MouseEvent<HTMLElement, MouseEvent>,
    ) => {
        function isTouchDevice() {
            return "ontouchstart" in window || navigator.maxTouchPoints > 0;
        }

        if (isTouchDevice()) {
            return;
        }
        setPopoverPositionX(event.clientX);
        setPopoverPositionY(event.clientY);
        setPopoverIsOpen(true);
    };

    const handlePopoverClose = () => {
        setPopoverIsOpen(false);
    };

    // Opening the booking search or damage overview over VehicleInfoModal causes a dispobody rerender, making lineItem undefined until it is closed
    // After closing the Popup everything works as intended again but trying to change certain values while lineItem is undefined causes a crash
    if (!lineItem) {
        return <></>
    }

    let enddate =
        lineItem.pickupDate || lineItem?.enddate || lineItem?.plannedEnddate;
    let startdate = lineItem.deliveryDate || lineItem?.startdate;

    if (moment.utc(startdate).isBefore(moment.utc(dispositionStartdate))) {
        if (moment.utc(enddate).isBefore(moment.utc(dispositionStartdate))) {
            startdate = enddate;
        } else {
            startdate = moment.utc(dispositionStartdate);
        }
    } else {
        startdate = moment.utc(startdate);
    }

    const calendarEnd = moment.utc(dispositionEnddate).add(1, "day");

    if (moment(enddate).isAfter(calendarEnd)) {
        enddate = moment.utc(dispositionEnddate).add(1, "day");
    }

    const moveEquipmentCheck = () => {


    }

    const reservationStartdate = moment.utc(startdate);
    const dispoStartdate = moment.utc(dispositionStartdate);
    const reservationEnddate = moment.utc(enddate, true);

    const dayWidthInPixel = ((dayWidth * contentWidth) / 100) * singleDay;

    const offsetDays = reservationStartdate.diff(dispoStartdate, "days", true);
    const leftPos = offsetDays * dayWidthInPixel;
    const partialInvoiceDate = lineItem?.partialInvoiceDate ? moment(lineItem?.partialInvoiceDate).add(1, "days") : null;

    const partialInvoiceDateDuration = partialInvoiceDate ? partialInvoiceDate.diff(reservationStartdate, "days", true) *
        dayWidthInPixel : null;

    const duration =
        reservationEnddate.diff(reservationStartdate, "days", true) *
        dayWidthInPixel;

    const customerName = lineItem?.customer?.name1;
    let driverName: string | null = null;
    if (lineItem?.driver1 !== null) {
        driverName =
            lineItem?.driver1?.firstname + " " + lineItem?.driver1?.lastname;
    }

    const [{opacity, isDragging}, drag, preview] = useDrag(
        () => ({
            type: "booking",
            item: {
                // @ts-ignore
                contentWidth: contentWidth + STATIC_COL_WIDTH,
                bookingId: lineItem?.bookingId,
                booking: lineItem,
                origin,
            },
            canDrag(monitor) {
                return lineItem?.isDraggable && !hasMinimalRights;
            },
            end(item, monitor) {
                const dropResult: any = monitor.getDropResult();
                if (item && dropResult?.destination !== undefined) {
                    confirm({
                        description: t("moveBooking.confirmText2"),
                        allowClose: false,
                        content: (
                            <InformationConfirmDialog
                                textContent={
                                    dropResult.destination.conflictBookings !== null &&
                                    lineItem?.bookingId !== dropResult.destination.conflictBookings[0].id
                                        ? t("moveBooking.conflictConfirmation")
                                        : t("moveBooking.confirmationText")
                                }
                                headline={t("moveBooking.moveBooking")}
                            />
                        ),
                    })
                        .then(() => {
                            if (dropResult.destination.conflictBookings !== null) {
                                dropResult.destination.conflictBookings.forEach((conflict) => {
                                    dispatch({
                                        type: MOVE_BOOKING,
                                        payload: {
                                            bookingId: conflict.id,
                                            vehicleId: null,
                                            stationId: conflict.station
                                                ? conflict.station
                                                : dropResult.destination.stationId,
                                        },
                                    });

                                    MoveBookingMutation(
                                        {
                                            bookingId: conflict.id,
                                            destinationVehicleId: null,
                                        },
                                        (err, result) => {
                                            if (err) {
                                                displayMessage("error", t("moveBooking.messages." + err.message));
                                            } else {
                                                MoveBookingMutation(
                                                    {
                                                        bookingId: item.bookingId,
                                                        destinationVehicleId: dropResult.destination.vehicleId,
                                                    },
                                                    (err, result) => {
                                                        if (err) {
                                                            displayMessage("error", t("moveBooking.messages." + err.message));
                                                        }
                                                    },
                                                );
                                            }
                                        },
                                    );
                                });
                            } else {
                                MoveBookingMutation(
                                    {
                                        bookingId: item.bookingId,
                                        destinationVehicleId: dropResult.destination.vehicleId,
                                    },
                                    (err, result) => {
                                        if (err) {
                                            displayMessage("error", t("moveBooking.messages." + err.message));
                                        } else {
                                            dispatch({
                                                type: MOVE_BOOKING,
                                                payload: {
                                                    bookingId: lineItem?.bookingId,
                                                    vehicleId: dropResult.type === "vehicle"
                                                        ? dropResult.destination.vehicleId
                                                        : null,
                                                    stationId: dropResult.type === "station"
                                                        ? dropResult.destination.stationId
                                                        : null,
                                                },
                                            });
                                        }
                                    },
                                );
                            }
                        })
                        .catch(() => {
                            if (dropResult.destination.conflictBookings !== null) {
                                dropResult.destination.conflictBookings.forEach((conflict) => {
                                    dispatch({
                                        type: MOVE_BOOKING,
                                        payload: {
                                            bookingId: conflict.id,
                                            vehicleId: conflict.vehicleId != null ? conflict.vehicleId : null,
                                            stationId: conflict.vehicleId == null ? conflict : null,
                                        },
                                    });
                                });
                            }

                            dispatch({
                                type: MOVE_BOOKING,
                                payload: {
                                    bookingId: item.bookingId,
                                    vehicleId:
                                        item?.booking?.vehicle != null
                                            ? item.booking.vehicle
                                            : null,
                                    stationId:
                                        item?.booking?.vehicle == null
                                            ? item.booking?.startstation || item.booking?.startstation?.stationId
                                            : null,
                                },
                            });
                        });
                }
            },
            collect: (monitor) => {
                return {
                    opacity: monitor.isDragging() ? 0.4 : 1,
                    isDragging: monitor.isDragging(),
                };
            },
        }),
        [name],
    );

    useEffect(() => {
        preview(getEmptyImage(), {captureDraggingState: true});
    }, []);

    const hasPartialInvoiceAndNotBilled = (): boolean => {
        if (lineItem?.isBilled) {
            return false;
        }

        return !!partialInvoiceDateDuration;
    }

    const getBorderColor = (delivery: boolean) => {
        if (delivery) {
            if (lineItem?.deliveryPrivate && lineItem?.deliveryDate && !lineItem?.delivered) {
                return deliveryAndPickupPrivateColor;
            }

            if (lineItem?.delivered) {
                return "#008000";
            }

            if (lineItem?.deliveryDate) {
                return "#FF4242";
            }
        } else {
            if (lineItem?.pickupPrivate && lineItem?.pickupDate && !lineItem?.readyForPickup) {
                return deliveryAndPickupPrivateColor;
            }

            if (lineItem?.readyForPickup) {
                return "#008000";
            }

            if (lineItem?.pickupDate) {
                return "#FF4242";
            }
        }

        return lineItem?.frameColor
            ? lineItem?.frameColor
            : lineItem?.contractColor;
    }

    const getStyle = () => {
        // define standard style
        return {
            width: duration,
            left: leftPos,
            background: hasPartialInvoiceAndNotBilled() ? `linear-gradient(to right, ${partialInvoiceColor} 0px, ${partialInvoiceColor} ${partialInvoiceDateDuration}px, ${lineItem?.contractColor} ${partialInvoiceDateDuration}px, ${lineItem?.contractColor} ${partialInvoiceDateDuration}px)` : lineItem?.contractColor,
            borderWidth: "0px 6px 0px 6px",
            borderStyle: "solid",
            paddingTop: "3px",
            paddingBottom: "3px",
            borderColor: lineItem?.frameColor
                ? lineItem?.frameColor
                : lineItem?.contractColor,
            borderLeftColor: getBorderColor(true),
            borderRightColor: getBorderColor(false),
            display: moment.utc(lineItem?.enddate).isBefore(dispoStartdate)
                ? "none"
                : "block",
        };
    };

    const renderLockIcon = () => {
        if (lineItem?.isDraggable || !lineItem?.mustBe) {
            return <></>;
        }
        return (
            <div style={{position: "absolute"}}>
                <LockIcon
                    fontSize={"small"}
                    style={{width: "0.75em", height: "0.75em"}}
                />
            </div>
        );
    };

    return (
        <>
            <div
                ref={drag}
                data-testid={"clickBookingline-" + lineItem?.bookingId}
                className={classes.draggable}
                data-id={lineItem?.bookingId}
                data-searchresult={lineItem?.isSearchResult}
                onClick={(e) => {
                    logger.log(
                        "Disposition: user clicked booking " +
                        lineItem?.bookingId,
                    );
                    e.preventDefault();
                    e.stopPropagation();
                    dispatch({
                        type: OPEN_BOOKING_INFO,
                        data: {item: lineItem},
                    });
                }}
                onMouseMove={(e) => {
                    handlePopoverOpen(e);
                }}
                onMouseLeave={handlePopoverClose}
                style={{...getStyle(), opacity}}
            >
                {renderLockIcon()}
                <div
                    className={classes.booking}
                    style={{
                        fontSize: "11px",
                        lineHeight: 1.3,
                        padding: "0px 8px",
                        paddingLeft: 14,
                    }}
                >
                    {t("bookingLine.customer")}: {customerName}
                    {lineItem?.reservationNumber ? (
                        <> - ResNr. {lineItem?.reservationNumber}</>
                    ) : (
                        <></>
                    )}{" "}
                    <br/>
                    {t("bookingLine.driver")}: {driverName ? driverName : "N/A"}
                    <div>
                        {lineItem?.startstation && (
                            <span>
                                {lineItem?.startstation?.stationId || lineItem?.startstation}
                                <ArrowForwardIcon
                                    style={{
                                        width: 20,
                                        height: 12,
                                        paddingTop: 3,
                                    }}
                                />
                                {lineItem?.endstation && lineItem?.enddate ? (
                                    <>{lineItem?.endstation?.stationId || lineItem?.endstation}</>
                                ) : (
                                    <>
                                        {lineItem?.plannedEndstation?.stationId || lineItem?.plannedEndstation}
                                    </>
                                )}
                            </span>
                        )}
                    </div>
                </div>
            </div>
            {popoverIsOpen && (
                <BookingPopover
                    lineItem={lineItem}
                    popoverPositionX={popoverPositionX}
                    popoverPositionY={popoverPositionY}
                    open={popoverIsOpen && !isDragging}
                    handlePopoverClose={handlePopoverClose}
                />
            )}
        </>
    );
};

export default React.memo(BookingLine);
